import React, {useState, useEffect, useRef, useReducer, useMemo} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {verifyUser, logoutUser, jwtDecode} from "../../authRequests";
import {crud, loadDataForPatientLogin} from "../../crudRequests";
import {patientReducer, init} from "../../additional_files/reducer";
import {useSocket} from "../../additional_files/custom";
import Layout from "../Layout";
import {
  Box,
  Button,
  Heading,
  Input,
  Text,
  VStack,
  Flex,
  HStack,
  Switch,
} from "@chakra-ui/react";
import {useToast} from "@chakra-ui/react";
import DateStrip, {dateToString} from "../DateStrip";
import moment from "moment";

const Prescription = ({item, setPrescriptions, index, isLast}) => {
  const updateItem = (key, value) => {
    setPrescriptions((prev) => {
      let thisItem = prev.filter((entry) => entry.presId === item.presId)[0];
      thisItem[key] = value;
      let prevCopy = [...prev];
      prevCopy[index] = thisItem;

      return prevCopy;
    });
  };

  return (
    <Box
      w="100%"
      py={4}
      borderBottomWidth={isLast ? 0 : 1}
      borderColor="gray.50"
    >
      <Flex align="center" justify="space-between">
        <Box mr={4} flex={1}>
          <Text fontSize="sm" fontWeight="500" color="#1a1f36">
            {item?.drugName || ""}
          </Text>
          <Text fontSize="xs" color="#6b7280" mt={0.5}>
            {item?.dosage}
          </Text>
        </Box>
        <Flex align="center" gap={3}>
          <Text fontSize="xs" color={item.taken ? "green.500" : "gray.400"} fontWeight="500">
            {item.taken ? "Taken" : "Missed"}
          </Text>
          <Switch
            isChecked={!!item.taken}
            colorScheme="purple"
            size="md"
            onChange={(e) => {
              const on = e.target.checked;
              updateItem("taken", on);
              if (on) updateItem("reasoning", "");
            }}
          />
        </Flex>
      </Flex>
      {!item.taken && (
        <Box mt={3}>
          <Input
            size="sm"
            value={item?.reasoning || ""}
            onChange={(e) => updateItem("reasoning", e.target.value)}
            placeholder="Reason (optional) — e.g., side effects, forgot, ran out"
            borderColor="gray.200"
            _hover={{borderColor: "gray.300"}}
            _focus={{borderColor: "#5754FF", boxShadow: "0 0 0 1px #5754FF"}}
          />
        </Box>
      )}
    </Box>
  );
};

const useFormData = (initialData) => {
  const [data, setData] = useState(initialData);

  const updateData = (name) => (value) => {
    setData((prev) => ({...prev, [name]: value}));
  };

  const getData = (name) => {
    return data[name];
  };

  return [getData, updateData, data, setData];
};

export default function PrescriptionCompliance() {
  const {state} = useLocation();
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const toast = useToast();
  const selectedAppointment = useRef(null);
  selectedAppointment.current = dashState.selectedAppointment;

  const loadedRef = useRef(null);
  const id = state && jwtDecode(state.jwt).id;

  const [validDates, setValidDates] = useState([new Date()]);

  const queryValidDates = (id) => {
    crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "prescription_compliance",
        parameters: [{pid: id}],
        method: "find",
      },
    ]).then((res) => {
      const dates = res.data[0].map((entry) => entry.date);
      console.log({dates});
      setValidDates([...dates, dateToString(new Date())]);
    });
  };

  // const upsertEmptyJournal = async (id, date) => {
  //   await crud(state, [
  //     {
  //       db: dashState?.db || "blh",
  //       collection: "prescription_compliance",
  //       parameters: [
  //         {pid: id, date: date},
  //         {
  //           $setOnInsert: {complianceEntries: []},
  //         },
  //         {upsert: true},
  //       ],
  //       method: "updateOne",
  //     },
  //   ]);
  // };

  useEffect(() => {
    verifyUser(state ? state.jwt : "", loadedRef);

    const date = new Date();

    // upsertEmptyJournal(id, dateToString(date)).then(() => {
    //   setDate(date);
    //   queryValidDates(id);
    // });
    setDate(date);
    queryValidDates(id);
  }, []);

  const [activeDate, setDate] = useState(new Date());

  const [getData, updateData, data, setData] = useFormData([]);

  const upsertEntry = async (id, date) => {
    await crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "prescription_compliance",
        parameters: [
          {pid: id, date: date},
          {
            $set: {
              complianceEntries: prescriptions,
              date: date,
              pid: id,
            },
          },
          {upsert: true},
        ],
        method: "updateOne",
      },
    ]);
  };

  const handleSubmit = () => {
    const dayStr = dateToString(moment(activeDate));
    toast.promise(upsertEntry(id, dayStr), {
      success: {title: "Success", description: "Entry recorded"},
      error: {title: "Please try again", description: "Something wrong"},
      loading: {title: "Loading...", description: "Please wait"},
    });
    setValidDates((prev) => (prev.includes(dayStr) ? prev : [...prev, dayStr]));
  };

  const loadPrescriptions = async () => {
    let res = await crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "prescriptions",
        parameters: [{pid: id}],
        method: "find",
      },
    ]);
    const entry = res?.data?.[0];

    // console.log({prescriptions: entry});
    if (entry.length > 0) {
      const notExpired = entry?.filter((item) => !item.expired);
      // if (dateToString(activeDate) === dateToString(new Date())) {
      setPrescriptions(
        notExpired?.map((item) => ({
          presId: item?.presId,
          drugName: item?.drugName,
          dosage: item?.dosage,
          taken: true,
          reasoning: "",
        }))
      );
      // }
    }
  };

  const loadEntry = async (id, date) => {
    let res = await crud(state, [
      {
        db: dashState?.db || "blh",
        collection: "prescription_compliance",
        parameters: [{pid: id, date: date}],
        method: "findOne",
      },
    ]);
    const entry = res?.data?.[0];
    //console.log({entry, date, res: res.data});
    if (entry) {
      setPrescriptions(entry?.complianceEntries || []);
      console.log({p: entry?.complianceEntries});
      if (!entry?.complianceEntries || entry?.complianceEntries.length === 0) {
        loadPrescriptions();
      }
    }
    if (!entry) {
      loadPrescriptions();
    }
  };
  const [prescriptions, setPrescriptions] = useState([]);

  useEffect(() => {
    loadEntry(id, dateToString(activeDate));
  }, []);

  const todayStr = dateToString(new Date());
  const selectedStr = dateToString(activeDate || new Date());
  const takenCount = prescriptions?.filter((p) => p.taken).length || 0;
  const totalCount = prescriptions?.length || 0;

  const handleDateSelect = (d, dStr) => {
    setDate(d);
    loadEntry(id, dStr);
  };

  const handleGoToToday = () => {
    const today = new Date();
    setDate(today);
    loadEntry(id, todayStr);
  };

  return (
    <Layout state={{...dashState}}>
      <VStack
        ref={loadedRef}
        style={{opacity: 0}}
        flex={1}
        w="100%"
        overflow={"auto"}
        alignItems="stretch"
      >
        <Box w="100%" px={{base: 4, md: 6}} py={5}>
          {/* Page header with compliance inline */}
          <Flex
            align={{base: "flex-start", md: "center"}}
            justify="space-between"
            mb={5}
            direction={{base: "column", md: "row"}}
            gap={3}
          >
            <Box>
              <Heading fontSize="xl" fontWeight="600" color="#1a1f36">
                Prescriptions
              </Heading>
              <Text fontSize="13px" color="#6b7280" mt={1}>
                Log your daily medication compliance
              </Text>
            </Box>
            {totalCount > 0 && (
              <HStack
                bg="white"
                borderWidth="1px"
                borderColor="gray.100"
                rounded="lg"
                px={4}
                py={2.5}
                spacing={3}
              >
                <Text fontSize="xs" color="gray.500">
                  Compliance
                </Text>
                <Box
                  w="60px"
                  h="4px"
                  bg="gray.100"
                  rounded="full"
                  overflow="hidden"
                >
                  <Box
                    h="100%"
                    w={`${totalCount > 0 ? (takenCount / totalCount) * 100 : 0}%`}
                    bg={takenCount === totalCount ? "green.400" : "#5754FF"}
                    rounded="full"
                    transition="width 0.3s"
                  />
                </Box>
                <Text fontSize="sm" fontWeight="600" color={takenCount === totalCount ? "green.500" : "#1a1f36"}>
                  {takenCount}/{totalCount}
                </Text>
              </HStack>
            )}
          </Flex>

          <DateStrip
            activeDate={activeDate}
            onDateSelect={handleDateSelect}
            completedDates={validDates}
            onGoToToday={handleGoToToday}
          />

          {/* Medications card */}
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.100"
            rounded="lg"
            px={5}
            py={4}
            mb={5}
          >
            <Heading as="h3" fontSize="sm" fontWeight="600" mb={2} color="#1a1f36">
              Medications
            </Heading>
            {prescriptions?.length === 0 && (
              <Box py={6} textAlign="center">
                <Text fontSize="sm" color="gray.400">
                  No active prescriptions found
                </Text>
              </Box>
            )}
            {prescriptions?.map((item, i) => (
              <Prescription
                item={item}
                key={i}
                setPrescriptions={setPrescriptions}
                index={i}
                isLast={i === prescriptions.length - 1}
              />
            ))}
          </Box>

          {/* Submit */}
          {totalCount > 0 && (
            <Flex mb={6} align="center" gap={3}>
              <Button
                onClick={handleSubmit}
                bg="#5754FF"
                color="white"
                size="sm"
                px={6}
                fontWeight="500"
                _hover={{bg: "#4644e0"}}
                _active={{bg: "#3a38c8"}}
              >
                Save entry
              </Button>
              {validDates?.includes?.(selectedStr) && (
                <Text fontSize="xs" color="green.500" fontWeight="500">
                  Entry saved for this date
                </Text>
              )}
            </Flex>
          )}
        </Box>
      </VStack>
    </Layout>
  );
}
